import { useEffect } from 'react';

import {
  endSession,
  getAccessToken,
  getTokenExpiryMs,
  IDLE_TIMEOUT_MS,
  isIdle,
  recordActivity,
} from '@/lib/session';

/** Eventos que cuentan como actividad del operador. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
/** Cada cuánto se revisa la inactividad (también al volver a la pestaña). */
const CHECK_INTERVAL_MS = 30 * 1000;
/** No escribir en sessionStorage en cada evento: basta una marca cada 5 s. */
const ACTIVITY_THROTTLE_MS = 5 * 1000;

/**
 * OS-1710 — cierra la sesión de la consola:
 * - por inactividad (`IDLE_TIMEOUT_MS`, 30 min sin interacción) → `/login?idle=1`;
 * - al expirar el access token, sin esperar a que una petición devuelva 401 → `/login?expired=1`.
 *
 * La última actividad vive en sessionStorage, así que recargar la página no reinicia el contador.
 */
export function useSessionTimeout(idleTimeoutMs: number = IDLE_TIMEOUT_MS): void {
  useEffect(() => {
    let lastWrite = 0;

    function onActivity(): void {
      const now = Date.now();
      if (now - lastWrite >= ACTIVITY_THROTTLE_MS) {
        lastWrite = now;
        recordActivity(now);
      }
    }

    function check(): void {
      const token = getAccessToken();
      const expiry = token ? getTokenExpiryMs(token) : null;
      if (!token || expiry === null || expiry <= Date.now()) {
        endSession('expired');
        return;
      }
      if (isIdle(Date.now(), idleTimeoutMs)) {
        endSession('idle');
      }
    }

    // Una sesión recién restaurada sin marca de actividad empieza a contar desde ahora.
    if (!isIdle(Date.now(), idleTimeoutMs)) {
      onActivity();
    }
    check();

    const token = getAccessToken();
    const expiry = token ? getTokenExpiryMs(token) : null;
    const expiryTimer =
      expiry !== null ? window.setTimeout(check, Math.max(0, expiry - Date.now())) : undefined;
    const interval = window.setInterval(check, CHECK_INTERVAL_MS);

    function onVisibility(): void {
      if (document.visibilityState === 'visible') {
        check();
      }
    }

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(interval);
      if (expiryTimer !== undefined) {
        window.clearTimeout(expiryTimer);
      }
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [idleTimeoutMs]);
}
